import { QueryKey, useMutation, useQuery } from "@tanstack/react-query";
import { ERRORS } from "../consts";
import { fetcherFactory } from "../services";
import { useAuthStore } from "../stores";
import type { CampaignDto, CampaignScoresDto } from "../types";
import { getEnvironmentVariables } from "../utils/general";

const { basePath } = getEnvironmentVariables();

export function useCampaignsQuery({ enabled = true }) {
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const initiativeId = useAuthStore(state => state.userData?.initiative_id) ?? "";
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const fetcher = fetcherFactory();
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const uri = `/usermgmt/${corporateId}/collect-campaigns`;
  return useQuery<unknown, unknown, {
    metadata: {
      total_pages: number,
      total_elements: number
    },
    output: CampaignDto[]
  }, QueryKey>({
    enabled: Boolean(enabled
      && accessToken
      && initiativeId
      && sessionToken
      && corporateId
    ) && !isSeedingSession,
    queryKey: [uri],
    queryFn: async () => {
      const pageSize = 100;
      let page = 0;
      let allItems: CampaignDto[] = [];
      let totalPages = 1;
      try {
        while(page<totalPages) {
          const response = await fetcher(
            `${basePath}${uri}?page=${page}&page_size=${pageSize}`,
            {
              headers: {
                "accept": "application/json",
                "Authorization": accessToken,
                "x-ada-session-token": sessionToken
              }
            });
          const result : {
            metadata: {
              total_pages: number,
              total_elements: number
            },
            output: CampaignDto[]
          } = await response.json();
          allItems.push(...result.output);
          totalPages = result.metadata.total_pages;
          page++;
        }
        return {
          metadata: {
            total_pages: totalPages,
            total_elements: allItems.length
          },
          output: allItems
        };
      } catch(error) {
        if (error === ERRORS.UNAUTHORIZED) {
          setUnauthorized(true);
          throw error; // rethrow so that react query doesn't complain about undefined return value
        } else {
          throw error;
        }
      }
    },
    refetchOnMount: true,
    staleTime: 3 * 60 * 1000
  });

}

export function useCampaignScoresQuery({ enabled = true }) {
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const initiativeId = useAuthStore(state => state.userData?.initiative_id) ?? "";
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const fetcher = fetcherFactory();
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const uri = `/usermgmt/${corporateId}/collect-campaigns/scores`;
  return useQuery<unknown, unknown, CampaignScoresDto[], QueryKey>({
    enabled: Boolean(enabled
      && accessToken
      && initiativeId
      && sessionToken
      && corporateId
    ) && !isSeedingSession,
    queryKey: [uri],
    queryFn: async () => {
      try {
        const response = await fetcher(
          `${basePath}${uri}`,
          {
            headers: {
              "accept": "application/json",
              "Authorization": accessToken,
              "x-ada-session-token": sessionToken
            }
          });
        const result : CampaignScoresDto[] = await response.json();
        return result;
      } catch(error) {
        if (error === ERRORS.UNAUTHORIZED) {
          setUnauthorized(true);
          throw error; // rethrow so that react query doesn't complain about undefined return value
        } else {
          throw error;
        }
      }
    },
    refetchOnMount: true,
    staleTime: 3 * 60 * 1000
  });

}

export function useDownloadCampaignCertificates() {
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const fetcher = fetcherFactory();
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const userId = useAuthStore(state => state.userData?.id_user) ?? "";
  return useMutation({
    mutationFn: () => fetcher(
      `${basePath}/usermgmt/${corporateId}/collect-campaigns/certificate/${userId}/download`,
      {
        headers:{
          "authorization": idToken,
          "x-ada-session-token": sessionToken
        },
        method: "GET"
      }).then( (res) => {
      return res.blob();
    })
      .catch(async (error) => {
        const errorRes = await error;
        throw errorRes;
      })
  });
}
